import type React from 'react'

import { Textarea } from '@/components/ui/textarea'
import { TEXT_ON_PATH_PLACEHOLDER } from '@/lib/shapes/text-on-path-session'

import { CARD_HEIGHT, CARD_WIDTH } from '../constants/toolbar-dimensions'

interface TextCardProps {
  textareaRef: React.Ref<HTMLTextAreaElement>
  text: string
  /** Approximate number of characters that fit along the path, shown as the counter's total. */
  charBudget: number
  onTextChange: (text: string) => void
}

/** The text card stacked above the control pill: the text field plus its character counter. */
export function TextCard({ textareaRef, text, charBudget, onTextChange }: TextCardProps) {
  return (
    <div
      className="flex flex-col rounded-md border border-border bg-popover px-3 py-2 shadow-md"
      style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
    >
      {/*
        The field flexes to fill the card instead of being pinned at 42px, which left ~10px
        of dead space under it and read as a card too tall for its contents — the other
        half of the "larger than the design" report.

        `min-h-0` is required: `ui/textarea.tsx` carries `field-sizing-content`, so a flex
        item's automatic minimum size would let a long value push the card taller than
        `CARD_HEIGHT`. With the minimum released, `overflow-y-auto` scrolls instead.
      */}
      <Textarea
        ref={textareaRef}
        autoFocus
        value={text}
        placeholder={TEXT_ON_PATH_PLACEHOLDER}
        onChange={(e) => onTextChange(e.target.value)}
        aria-label="Text on a path"
        className="min-h-0 flex-1 resize-none overflow-y-auto border-none px-0 py-0 shadow-none focus-visible:border-none focus-visible:ring-0"
      />
      {/*
          Counter only — no `maxLength`. The total is an estimate of what fits along the path, not
          a limit, so typing past it is allowed and the text is drawn as far as the path goes.
        */}
      <div className="text-right text-xs leading-none text-muted-foreground">
        {text.length} / {charBudget}
      </div>
    </div>
  )
}
